import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { API_BASE } from "../../app/const"
import { fireauth } from "../auth/AuthSlice"
import { RootState } from "../../app/store"

export interface ApiTokenObj {
  apiAccessId: number
  apiKey: string
  maskedApiKey: string
  expiry: string
  version: string
  description: string
  validDomains: string
  ownerId: string
  ownerEmail: string

  callbackEmail?: string
  callbackUrl?: string
  callbackSharedSecret?: string
}

export interface ApiTokenState {
  status: 'idle' | 'loading' | 'failed'
  visible: boolean
  token: ApiTokenObj
  tokens: ApiTokenObj[]
  embedCode?: string
}

const initialState: ApiTokenState = {
  status: 'idle',
  visible: false,
  token: {
    apiAccessId: 0,
    apiKey: '',
    maskedApiKey: '',
    description: '',
    validDomains: '*',
    version: '',
    ownerId: '',
    ownerEmail: '',
    expiry: ''
  },
  tokens: []
}

export const fetchAllApiTokensAsync = createAsyncThunk(
  'account/fetchAllApiTokensAsync', async (args: any, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/api/keys`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const fetchConfigDetailsAsync = createAsyncThunk(
  'account/fetchConfigDetailsAsync', async ({ slug, mode }: { slug: string, mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/config/${mode}/bys/${slug}`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const saveApiTokenAsync = createAsyncThunk(
  'admin/saveConfigAsync', async ({ apiToken }: { apiToken: ApiTokenObj }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.post(`${API_BASE}apps/api/key`, {
        description: apiToken.description,
        validDomains: apiToken.validDomains,
        callbackEmail: apiToken.callbackEmail,
        callbackUrl: apiToken.callbackUrl,
        callbackSharedSecret: apiToken.callbackSharedSecret
      }, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const deleteApiTokenAsync = createAsyncThunk(
  'admin/deleteApiTokenAsync', async ({ apiToken }: { apiToken: ApiTokenObj }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.delete(`${API_BASE}apps/api/key/${apiToken.apiAccessId}`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const getEmbedCodeAsync = createAsyncThunk(
  'admin/getEmbedCodeAsync', async ({ googleMapsApiKey, rentfinderApiBaseUrl, rentfinderApiToken, type }:
    {
      googleMapsApiKey: string, rentfinderApiBaseUrl: string, rentfinderApiToken: string, type: 'simple' | 'withPreContact' | 'withPostContact'
    }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.get(type === 'simple' ? `embed1.html`
      : type === 'withPreContact' ? `embed2.html`
        : `embed3.html`, {
      headers: {
        "Content-Type": "application/text; charset=UTF-8"
      }
    })
    return response.data
      .replace('REPLACEME#rentfinderApiBaseUrl', rentfinderApiBaseUrl)
      .replace('REPLACEME#rentfinderApiToken', rentfinderApiToken)
      .replace('REPLACEME#googleMapsApiKey', googleMapsApiKey)
  }
}
)

export const apiTokenSlice = createSlice({
  name: 'api-token',
  initialState,
  reducers: {
    resetApiToken: (state) => {
      state.status = 'idle'
      state.token = initialState.token
    },
    pickApiToken: (state, action) => {
      state.status = 'idle'
      state.token = action.payload
    },
    hideToken: (state) => {
      state.status = 'idle'
      state.visible = false
    },
    clearEmbedCode: (state) => {
      state.status = 'idle'
      state.embedCode = ''
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(fetchAllApiTokensAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllApiTokensAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.tokens = action.payload
      }).addCase(fetchAllApiTokensAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.tokens = []
      }).addCase(saveApiTokenAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(saveApiTokenAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.token = action.payload
        state.visible = true
        state.tokens = [...state.tokens, action.payload]
      }).addCase(saveApiTokenAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.token = initialState.token
      }).addCase(deleteApiTokenAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(deleteApiTokenAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.tokens = state.tokens.filter((token) => token.apiAccessId !== action.payload.apiAccessId)
      }).addCase(deleteApiTokenAsync.rejected, (state, action) => {
        state.status = 'failed'
      }).addCase(getEmbedCodeAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(getEmbedCodeAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.embedCode = action.payload
      }).addCase(getEmbedCodeAsync.rejected, (state, action) => {
        state.status = 'failed'
      })
  }
})

export const { resetApiToken, pickApiToken, hideToken, clearEmbedCode } = apiTokenSlice.actions
export const selectAllApiTokens = (state: RootState) => state.apiToken.tokens
export const selectApiToken = (state: RootState) => state.apiToken.token
export const selectApiTokenVisible = (state: RootState) => state.apiToken.visible
export const selectEmbedCode = (state: RootState) => state.apiToken.embedCode
export default apiTokenSlice.reducer
